import React from 'react';
import { Accordion, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { isArray } from 'lodash';

import { FormGroupControl } from '@components/Base';
import { getBMI } from '@utils/health';
import { GenderFormSelect } from '@components/Feature/User/gender-form-select';
import { NumericString } from '@utils/typescript';

import { FormValues } from './schema';

const GROUP_PROPS = { className: 'mb-3' };

type FormValuesKeys = keyof Omit<FormValues, 'healthPlanId'>;

type Props = {
  name: FormValuesKeys | `${FormValuesKeys}.${number}`;
  label: string;
};

const ContactFormControl = ({ name, label }: Props) => {
  const { t } = useTranslation();
  const { initialValues, values, setFieldValue } = useFormikContext<FormValues>();

  const handleBlur = () => {
    const [key, idx] = name.split('.') as [FormValuesKeys, NumericString];
    const value = values[key];

    if (!value) return;

    const { weight, height } = isArray(value) ? value[Number(idx)] : value;

    if (weight && height) {
      setFieldValue(`${name}.bmi`, getBMI(weight, height), true);
    }
  };

  return (
    <Accordion.Item eventKey={name} key={name}>
      <Accordion.Header>
        <span className="fw-bold">{label}</span>
      </Accordion.Header>
      <Accordion.Body className="d-grid gap-3">
        <Row>
          <Col>
            <FormGroupControl
              required
              name={`${name}.lastName`}
              label={t('user.lastName')}
              groupProps={GROUP_PROPS}
              disabled={name === 'me' && !!initialValues.me.lastName}
            />
          </Col>
          <Col>
            <FormGroupControl
              required
              name={`${name}.firstName`}
              label={t('user.firstName')}
              groupProps={GROUP_PROPS}
              disabled={name === 'me' && !!initialValues.me.firstName}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <FormGroupControl
              name={`${name}.lastNameKm`}
              label={t('user.lastNameKm')}
              groupProps={GROUP_PROPS}
              disabled={name === 'me' && !!initialValues.me.lastNameKm}
            />
          </Col>
          <Col>
            <FormGroupControl
              name={`${name}.firstNameKm`}
              label={t('user.firstNameKm')}
              groupProps={GROUP_PROPS}
              disabled={name === 'me' && !!initialValues.me.firstNameKm}
            />
          </Col>
        </Row>

        <Row>
          {name === 'me' && (
            <Col>
              <GenderFormSelect
                required
                name={`${name}.gender`}
                groupProps={GROUP_PROPS}
                disabled={name === 'me' && !!initialValues.me.gender}
              />
            </Col>
          )}

          <Col>
            <FormGroupControl
              required
              type="date"
              name={`${name}.dob`}
              label={t('user.dob')}
              groupProps={GROUP_PROPS}
              disabled
            />
          </Col>
        </Row>

        <Row xs={{ cols: 2 }}>
          <Col>
            <FormGroupControl
              required
              type="number"
              name={`${name}.weight`}
              label={`${t('common:weight')} (KG)`}
              groupProps={GROUP_PROPS}
              onBlur={handleBlur}
            />
          </Col>

          <Col>
            <FormGroupControl
              required
              type="number"
              name={`${name}.height`}
              label={`${t('common:height')} (CM)`}
              groupProps={GROUP_PROPS}
              onBlur={handleBlur}
            />
          </Col>
        </Row>

        <FormGroupControl required type="number" name={`${name}.bmi`} label="BMI" groupProps={GROUP_PROPS} disabled />

        <FormGroupControl
          name={`${name}.personalCode`}
          label={t('user.personalCode')}
          groupProps={GROUP_PROPS}
          disabled={name === 'me' && !!initialValues.me.personalCode}
        />

        <FormGroupControl
          required
          name={`${name}.occupation`}
          label={t('user.occupation')}
          groupProps={GROUP_PROPS}
          disabled={name === 'me' && !!initialValues.me.occupation}
        />

        {name === 'me' && (
          <>
            <FormGroupControl
              required
              name={`${name}.email`}
              label={t('user.email')}
              groupProps={GROUP_PROPS}
              disabled={!!initialValues.me.email}
            />
            <FormGroupControl
              required
              name={`${name}.mobileNumber`}
              label={t('user.mobileNumber')}
              groupProps={GROUP_PROPS}
              disabled={!!initialValues.me.mobileNumber}
            />
            <FormGroupControl
              required
              name={`${name}.address`}
              label={t('user.address')}
              groupProps={GROUP_PROPS}
              disabled={!!initialValues.me.address}
            />
          </>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

export { ContactFormControl };
